/**
 * Shared AudioContext singleton — used by tradeAudio and defenseAudio.
 *
 * Single AudioContext avoids browser limits on concurrent contexts.
 * Provides playTone() for simple sine waves and playSynthSound() for
 * multi-voice synthesized sounds with envelope shaping.
 */

export interface SynthVoice {
  type: OscillatorType | 'noise';
  freq: number;
  duration: number;
  amplitude: number;
  detune?: number;
  /** Sweep target frequency over duration */
  freqSweep?: number;
  /** Attack time in seconds (default 0.01) */
  attack?: number;
  /** Decay time in seconds — exponential ramp to silence (default = duration) */
  decay?: number;
}

let audioCtx: AudioContext | null = null;

export function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioCtx = new Ctor();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

function getMasterVolume(): number {
  try {
    const raw = localStorage.getItem('galactus:soundSettings');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.volume === 'number') return parsed.volume;
    }
  } catch {
    /* noop */
  }
  return 0.5;
}

export async function playTone(
  freqHz: number,
  durationSec: number,
  amplitude: number
): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) return;
  // Browsers start AudioContext in "suspended" state — must await resume before scheduling
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  const scaled = amplitude * getMasterVolume();
  if (scaled < 0.001) return; // effectively silent
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = freqHz;
  osc.type = 'sine';
  gain.gain.setValueAtTime(scaled, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + durationSec);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + durationSec);
}

/**
 * Play multiple synthesized voices simultaneously with envelope shaping.
 * Each voice gets its own oscillator + gain node routed to the destination.
 */
export async function playSynthSound(voices: SynthVoice[]): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  const master = getMasterVolume();
  if (master < 0.001) return;

  const now = ctx.currentTime;

  for (const v of voices) {
    const scaled = v.amplitude * master;
    if (scaled < 0.001) continue;

    const gain = ctx.createGain();
    gain.connect(ctx.destination);

    const attack = v.attack ?? 0.01;
    const decay = v.decay ?? v.duration;

    // Envelope: ramp up during attack, then exponential decay
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(scaled, now + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, now + attack + decay);

    if (v.type === 'noise') {
      // White noise via buffer source
      const bufferSize = ctx.sampleRate * v.duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buffer;
      noise.connect(gain);
      noise.start(now);
      noise.stop(now + v.duration);
    } else {
      const osc = ctx.createOscillator();
      osc.type = v.type;
      osc.frequency.setValueAtTime(v.freq, now);
      if (v.detune) osc.detune.setValueAtTime(v.detune, now);
      if (v.freqSweep) {
        osc.frequency.exponentialRampToValueAtTime(v.freqSweep, now + v.duration);
      }
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + v.duration);
    }
  }
}
